import { useEffect, useState, useCallback, useRef } from 'react';

export interface ApiHooksParams<P, D> {
  /**
   * data的初始值
   */
  initValue?: { [N in keyof D]?: D[N] } & any;
  /**
   * 请求的参数，参数变化会自动使用新的参数再次发起请求
   */
  params?: P;
  /**
   * 是否自动请求接口
   */
  needInit?: boolean;
  /**
   * 如果参数相同，是否使用缓存的上次data数据，取了新data会覆盖
   * 默认为：true
   */
  cache?: boolean | 'local' | 'session';
  /**
   * 相同的接口和参数只会请求一次
   */
  deepCache?: boolean;
  /**
   * 防抖间隔，频繁改变参数，则只发最后一次请求
   */
  debounceInterval?: number;
}

const cacheObj: {
  [name: string]: any;
} = {};
const deepCacheApis: string[] = [];
const loadingApis: { [name: string]: Promise<any> } = {};

export const createApiHooksConfig = {
  setCacheFn: (name: string, value: any) => (cacheObj[name] = value),
  getCacheFn: (name: string) => cacheObj[name]
};

// @ts-ignore
let sessionStorage = global.sessionStorage || {
  setItem: (name: string, value: any) => (cacheObj[name] = value),
  getItem: (name: string) => cacheObj[name]
}

// @ts-ignore
let localStorage = global.localStorage || (process.env.REMAX_PLATFORM === 'wecaht' ? wx.getStorageSync : process.env.REMAX_PLATFORM === 'ali' ? my.getStorageSync : sessionStorage);

const localStore = {
  setItem: (name: string, value: any) => {
    localStorage.setItem(name, JSON.stringify(value));
  },
  getItem: (name: string) => {
    const value = localStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  }
};

const sessionStore = {
  setItem: (name: string, value: any) => {
    sessionStorage.setItem(name, JSON.stringify(value));
  },
  getItem: (name: string) => {
    const value = sessionStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  }
};

export default function createApiHooks<P, D>(
  request: (params: P) => Promise<{ data: D }>
) {
  const getRequestString = (params: any) =>
    `${request.toString()}${
      typeof params === 'object' ? JSON.stringify(params) : params
    }`;
  const Hooks = ({
    cache = true,
    initValue = {},
    params,
    needInit = true,
    deepCache = false,
    debounceInterval
  }: ApiHooksParams<P, D> = {}) => {
    const cacheValue = cache === 'local' ? localStore.getItem(getRequestString(params)) : cache === 'session' ? sessionStore.getItem(getRequestString(params)) : null;
    initValue = (cacheValue !== null ? cacheValue : cache
      && createApiHooksConfig.getCacheFn(getRequestString(params)))
      || initValue;
    needInit = deepCache && typeof cache === 'string' && cacheValue !== null ? false : needInit;
    const initValueRef = useRef(initValue);
    const [error, setError] = useState<any>();
    const [data, setData] = useState<D>(initValue);
    const [loading, setLoading] = useState<boolean>();
    const preParams = useRef(initValue);
    const isOutRequestRef = useRef(false);
    const unmountRef = useRef(false);
    useEffect(
      () => () => {
        unmountRef.current = true;
      },
      []
    );
    const dataRef = useRef(data);
    dataRef.current = data;
    const innerRequest = useCallback(params => {
      const isOutRequest = isOutRequestRef.current;
      const requestString = getRequestString(params);
      const setCacheFn = {
        local: (name: string, value: any) => localStore.setItem(name, value),
        session: (name: string, value: any) => sessionStore.setItem(name, value),
      }[cache + ''] || createApiHooksConfig.setCacheFn;
      const getCacheFn = {
        local: (name: string) => localStore.getItem(name),
        session: (name: string) => sessionStore.getItem(name),
      }[cache + ''] || createApiHooksConfig.getCacheFn;
      if (
        (JSON.stringify(preParams.current) === JSON.stringify(params) &&
          !isOutRequest) ||
        (deepCache &&
          !isOutRequest &&
          getCacheFn(requestString) &&
          deepCacheApis.includes(requestString))
      )
        return Promise.resolve(dataRef.current);
      deepCacheApis.push(requestString);
      isOutRequestRef.current = false;
      if (!isOutRequest) {
        preParams.current = params && JSON.parse(JSON.stringify(params));
      }
      if (!deepCache || !getCacheFn(requestString)) {
        setLoading(true);
      }
      loadingApis[requestString] = (loadingApis[requestString]
        ? loadingApis[requestString].then(data => ({ data }))
        : request(params)
      )
        .then(response => {
          const data = response && response.data;
          dataRef.current = data;
          if (!unmountRef.current) {
            setError(undefined);
            setData(data);
          }
          if (data) {
            setCacheFn(
              getRequestString(params),
              typeof data !== 'string' ? JSON.parse(JSON.stringify(data)) : data
            );
          }
          return Promise.resolve(data);
        })
        .catch(e => {
          setData(initValueRef.current);
          if (!unmountRef.current) {
            setError(e || {});
          }
          if (isOutRequest) {
            preParams.current = {};
          }
          return Promise.reject(e);
        })
        .finally(() => {
          delete loadingApis[requestString];
          if (!unmountRef.current) {
            setLoading(false);
          }
        });
      return loadingApis[requestString];
    }, []);
    const preNeedInit = useRef(needInit);
    useEffect(() => {
      if (needInit !== preNeedInit.current) {
        preParams.current = {};
        preNeedInit.current = needInit;
      }
      if (!needInit) return;
      let timer: NodeJS.Timeout;
      if (debounceInterval) {
        timer = setTimeout(() => innerRequest(params), debounceInterval);
      } else {
        innerRequest(params);
      }
      return () => {
        clearTimeout(timer);
      };
    }, [needInit, params, innerRequest, debounceInterval]);
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const outRequest = useCallback<(p?: P) => Promise<D>>(
      (p = paramsRef.current) => {
        isOutRequestRef.current = true;
        return innerRequest(p);
      },
      [innerRequest, paramsRef]
    );
    return { request: outRequest, data, loading, error, dataRef };
  };
  const result: typeof Hooks & {
    request: (params?: P) => Promise<D>;
    DataType: D;
    ParamsType: P;
  } = Hooks as any;
  result.request = params => {
    const requestString = getRequestString(params);
    if (loadingApis[requestString]) {
      return loadingApis[requestString];
    }
    loadingApis[requestString] = request(params as P)
      .then(response => {
        const data = response && response.data;
        if (data) {
          createApiHooksConfig.setCacheFn(
            getRequestString(params),
            typeof data !== 'string' ? JSON.parse(JSON.stringify(data)) : data
          );
        }
        return Promise.resolve(response && response.data);
      })
      .finally(() => {
        delete loadingApis[requestString];
      });
    return loadingApis[requestString];
  };
  return result;
}
